/**
 * Demo Daily View-Limit State
 * =============================
 * Global @preact/signals store backing the DEMO tier's daily cell-view quota,
 * the persistent usage meter, and the conversion overlay (Slice B).
 *
 * Backend contract (Slice A — already committed):
 *   - Cell-detail success (200) responses carry, for DEMO users only:
 *       X-Demo-Views-Used, X-Demo-Views-Limit, X-Demo-Views-Reset-At
 *   - The Nth+1 distinct-cell/day fetch returns 429 with
 *       detail = { code:"demo_daily_view_limit", message, viewed_count,
 *                  daily_limit, reset_at }
 *   - GET /api/v1/products/demo/usage-summary →
 *       { viewed_count, daily_limit, remaining, reset_at, tier, is_admin,
 *         viewed_today:[{product_id,name,manufacturer,viewed_at}] }
 *     daily_limit / remaining are -1 when unlimited (PRO / admin).
 *
 * Backend is the source of truth: headers and the usage-summary override the
 * localStorage mirror. The mirror is keyed by the UTC day and reset on day
 * change so a stale counter never leaks across midnight (matches the backend's
 * UTC reset_at).
 */
import { signal, computed, effect } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'
import { subscriptionTier } from './userState.js'
import { publicConfig } from './publicConfig.js'

// -1 sentinel from the backend = unlimited (PRO / admin).
export const UNLIMITED = -1

/**
 * Fallback daily deep-dive cap for the always-visible DEMO meter. Used purely
 * for *display* when the authoritative limit isn't a usable positive number yet
 * — initial boot before the usage-summary resolves, a response that omits the
 * limit, an unlimited misconfig on a DEMO account, or a sync gap right after a
 * Redis/config reset. The backend stays the source of truth and overrides this
 * the instant it reports a real cap. Matches the demo tier's default start cap.
 */
export const DEFAULT_DEMO_LIMIT = 10

const STORAGE_KEY = 'batemo:demoLimit'

/**
 * Current UTC calendar day as YYYY-MM-DD. The backend resets the quota at
 * UTC midnight, so the mirror must key off the same boundary.
 */
export function utcDayKey(date = new Date()) {
  return date.toISOString().slice(0, 10)
}

// ====== SIGNALS ======
export const viewsUsed = signal(0)
export const viewsLimit = signal(UNLIMITED)
export const viewsRemaining = signal(UNLIMITED)
export const resetAtUtc = signal(null) // ISO string, e.g. "2026-07-02T00:00:00+00:00"
export const viewedToday = signal([]) // [{product_id,name,manufacturer,viewed_at}]

/**
 * Monotonically-increasing counter bumped by `applyUnlockResult()` on every
 * successful in-place unlock (Grid/Table/Plot). Consumers that need to react
 * to "a cell just got unlocked" — e.g. MainAppPage re-fetching the explorer
 * list so the newly-unlocked cell's exact premium values replace its teaser
 * placeholders — watch this signal instead of duplicating unlock plumbing.
 * Never bumped by anything that itself runs as a *result* of watching it
 * (the explorer reload doesn't call unlockCell/applyUnlockResult), so it
 * can't loop.
 */
export const demoUnlockVersion = signal(0)

/**
 * Set of product IDs unlocked today (derived from viewedToday), so Grid/Table/
 * Plot can all consult a single global source instead of re-deriving the same
 * lookup. Backed by the backend's viewed_today list — the source of truth.
 */
export const unlockedTodayIds = computed(
  () => new Set((viewedToday.value || []).map((v) => v.product_id))
)

/** True when `productId` was unlocked today (present in the global viewed-today set). */
export function isCellUnlockedToday(productId) {
  return unlockedTodayIds.value.has(productId)
}

/**
 * True once an AUTHORITATIVE per-account cap has landed — from the usage-summary
 * (`daily_limit`), an `X-Demo-Views-Limit` success header, or a 429 payload's
 * `daily_limit`. Until then the always-on meter shows a placeholder denominator
 * ("X of …") instead of the /config/public daily fallback, because that fallback
 * cannot know a first-48h account's higher first-day cap and would briefly show
 * the wrong (lower) tier — the classic "0 of 5" → "1 of 10" jump. Reset on
 * logout/day-change so the next boot re-hydrates. Display-gate only; it never
 * feeds counting/reset logic.
 */
export const viewsLimitHydrated = signal(false)

// UI-driver signals
export const demoLimitModalOpen = signal(false)
export const demoLimitToastOpen = signal(false)
// First limit-hit of the day → full modal; later same-day hits → slim toast.
export const limitHitOnceToday = signal(false)

// ── Export upsell (surface-limits slice) ──────────────────────────────
// Demo users explore freely; the take-away (export) is the single PRO gate.
// Attempting an export on any surface opens this upsell and records a signal.
export const exportUpsellOpen = signal(false)
export const exportUpsellSurface = signal(null) // 'free_plot' | 'radar' | 'cell_selection' | null

/**
 * Open the export upsell for a given surface. Called after a demo user attempts
 * an export; the actual export is suppressed by the caller.
 * @param {string} surface - 'free_plot' | 'radar' | 'cell_selection'
 */
export function openExportUpsell(surface) {
  exportUpsellSurface.value = surface || null
  exportUpsellOpen.value = true
}

export function closeExportUpsell() {
  exportUpsellOpen.value = false
}

// ── Metric upsell (metric-tiering slice) ──────────────────────────────
// Premium measured metrics (impedance, thermal, batch & aging, DC25) are
// tiered for demo users. Clicking a locked/teaser metric token opens this
// upsell, category-framed (electrical/thermal/quality/…). Same "one modal per
// session, then slim toast" restraint as the daily-limit overlay so we surface
// the value once and never nag.
export const metricUpsellOpen = signal(false)
export const metricUpsellCategory = signal(null) // 'electrical' | 'thermal' | 'quality' | …
// First metric-token click of the session → full modal; later ones → slim toast.
export const metricUpsellSeenOnce = signal(false)

/**
 * Open the metric upsell for a given metric category. First click of the session
 * gets the full modal (Peak-End moment); later clicks get a slim inline toast so
 * repeated exploration never nags.
 * @param {string} category - 'electrical' | 'thermal' | 'quality' | …
 */
export function openMetricUpsell(category) {
  metricUpsellCategory.value = category || null
  if (!metricUpsellSeenOnce.value) {
    metricUpsellSeenOnce.value = true
    metricUpsellOpen.value = true
  } else {
    metricUpsellOpen.value = false
    import('../components/shared/Toast.jsx')
      .then(({ showToast }) => {
        showToast(
          'Impedance, thermal, batch & aging are measured for every cell — unlock with PRO.',
          'Talk to our team',
          '/contact'
        )
      })
      .catch(() => {
        /* toast optional */
      })
  }
}

export function closeMetricUpsell() {
  metricUpsellOpen.value = false
}

/**
 * True when the current user is a DEMO-tier user, whose full catalog is
 * unlocked (all cells clickable, subject to the daily view quota) rather than
 * shadow-locked. Case-insensitive: the backend serves 'DEMO' but some paths
 * carry 'demo' (see FreePlotView.jsx).
 */
export function isDemoCatalogUnlocked() {
  return String(subscriptionTier.value || '').toLowerCase() === 'demo'
}

/**
 * The daily cap to *display* in the always-on DEMO meter. Returns the stored
 * limit when it's a usable positive number; otherwise falls back to
 * DEFAULT_DEMO_LIMIT so the meter never renders "X of null"/"X of 0"/"X of -1"
 * during a boot/sync gap or on an unlimited misconfig. Display-only — it never
 * feeds counting/reset logic (that stays on the raw viewsLimit signal).
 */
export function getEffectiveViewsLimit() {
  const l = viewsLimit.value
  if (Number.isFinite(l) && l > 0) return l
  // Boot/sync gap: the authoritative per-user cap (usage-summary / X-Demo-Views-*
  // headers) hasn't populated viewsLimit yet. Prefer the REAL demo cap the backend
  // published via /config/public (reflects the admin override, e.g. 5) over the
  // static DEFAULT_DEMO_LIMIT, so the always-on meter never shows a stale "of 10"
  // when the true cap is lower. DEFAULT_DEMO_LIMIT stays as the last-ditch guard.
  const cfg = Number(publicConfig.value?.demo_cell_view_daily_limit)
  if (Number.isFinite(cfg) && cfg > 0) return cfg
  return DEFAULT_DEMO_LIMIT
}

// ====== localStorage MIRROR (backend still wins) ======
function loadMirror() {
  try {
    const raw = safeLocal.get(STORAGE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw)
    // Drop the mirror if it belongs to a previous UTC day.
    if (!saved || saved.day !== utcDayKey()) return
    if (typeof saved.viewsUsed === 'number') viewsUsed.value = saved.viewsUsed
    // viewsLimit + viewsRemaining are deliberately NOT rehydrated. Both are
    // config-derived (the daily cap and its remainder), not day-scoped usage. A
    // stale mirrored cap — e.g. 5 from before an admin raised the limit to 10 —
    // would (a) pin the meter denominator to 5 (getEffectiveViewsLimit sees a
    // "valid" 5 and never falls back to the fresh /config/public cap) and (b) via
    // viewsRemaining<=0 → isAtDeepDiveLimit keep every cell LOCKED across a hard
    // reload, deadlocking the user out even though the backend now allows more.
    // Leaving both on the UNLIMITED sentinel lets getEffectiveViewsLimit() show the
    // fresh public cap, and the authoritative usage-summary / X-Demo-Views-* response
    // sets the real cap + remainder right after boot. Backend enforcement stays the
    // real quota gate in the meantime. (Opened cells still stay unlocked across
    // reload via the rehydrated viewedToday list, not viewsRemaining.)
    if (saved.resetAtUtc) resetAtUtc.value = saved.resetAtUtc
    if (Array.isArray(saved.viewedToday)) viewedToday.value = saved.viewedToday
    limitHitOnceToday.value = !!saved.limitHitOnceToday
  } catch (e) {
    // Corrupt mirror — ignore, backend will repopulate.
    console.warn('[demoLimitStore] Failed to load localStorage mirror:', e)
  }
}

loadMirror()

// Persist a compact snapshot keyed by the current UTC day.
effect(() => {
  try {
    const snapshot = {
      day: utcDayKey(),
      viewsUsed: viewsUsed.value,
      // viewsLimit + viewsRemaining intentionally omitted — config-derived, not
      // day-scoped; persisting them would re-pin a stale cap/lock across reloads
      // (see loadMirror()).
      resetAtUtc: resetAtUtc.value,
      viewedToday: viewedToday.value,
      limitHitOnceToday: limitHitOnceToday.value,
    }
    safeLocal.set(STORAGE_KEY, JSON.stringify(snapshot))
  } catch (e) {
    /* localStorage may be unavailable (private mode) — non-fatal. */
  }
})

// ====== CONTRACT APPLICATION ======

function toInt(v) {
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

/**
 * A limit is "valid" only when it's the explicit unlimited sentinel (-1) or a
 * finite positive number. Anything else (0, NaN, a stray negative) is a sync
 * artefact from a partial response and must NOT clobber a previously known-good
 * cap — otherwise the meter would flip to a bogus/hidden value mid-session.
 */
function isValidLimit(n) {
  return n === UNLIMITED || (Number.isFinite(n) && n > 0)
}

/**
 * Apply the X-Demo-Views-* headers from a successful cell-detail response.
 * Accepts a Headers instance or a plain {name:value} map (case-insensitive).
 * No-op when the demo headers are absent (non-demo users / other endpoints).
 */
export function applyUsageHeaders(headers) {
  if (!headers) return
  const get = (name) =>
    typeof headers.get === 'function'
      ? headers.get(name)
      : headers[name] ?? headers[name.toLowerCase()]

  const used = toInt(get('X-Demo-Views-Used'))
  const limit = toInt(get('X-Demo-Views-Limit'))
  const reset = get('X-Demo-Views-Reset-At')

  // If none of the demo headers are present this response isn't quota-bearing.
  if (used == null && limit == null && !reset) return

  // Stale-response guard (mirrors applyUsageSummary). viewsUsed is monotonically
  // NON-DECREASING within a UTC day — the backend never un-counts a deep-dive
  // mid-day; only the midnight reset drops it to 0. Compare opens several cells
  // at once (CompareTableView's Promise.all), whose /products/{id} header
  // responses resolve OUT OF ORDER: if the one carrying the LOWEST `used` lands
  // last, a blind assignment would clamp the meter to that stale value ("+1 for
  // 3 added cells" until a later usage-summary reconciled). So a header reporting
  // FEWER views than we already hold is stale and ignored; the meter then climbs
  // monotonically to the correct end value. A genuine reset (used === 0) is
  // always honoured.
  const usedIsStale = used != null && used !== 0 && used < viewsUsed.value
  if (used != null && !usedIsStale) viewsUsed.value = used
  // Only a valid cap (positive or the unlimited sentinel) overwrites the stored
  // limit; a 0/garbage value keeps the last-known-good so the DEMO meter never
  // drops to an unusable cap on a partial response.
  if (limit != null && isValidLimit(limit)) {
    viewsLimit.value = limit
    // Authoritative per-account cap → the meter may now show the real denominator.
    viewsLimitHydrated.value = true
  }
  if (reset) resetAtUtc.value = reset

  // Derive remaining from the EFFECTIVE (possibly retained) used count — never
  // from a rejected stale `used` — so a stale header can't leave a mismatched
  // remaining behind (mirrors applyUsageSummary's else-branch).
  if (limit != null && isValidLimit(limit) && limit !== UNLIMITED) {
    viewsRemaining.value = Math.max(0, limit - viewsUsed.value)
  } else if (limit === UNLIMITED) {
    viewsRemaining.value = UNLIMITED
  }
}

/**
 * Apply the GET /products/demo/usage-summary payload (authoritative snapshot).
 */
export function applyUsageSummary(json) {
  if (!json || typeof json !== 'object') return

  // Stale-response guard. viewsUsed is monotonically NON-DECREASING within a UTC
  // day (the backend never un-counts a deep-dive mid-day; only the midnight reset
  // drops it to 0). Several usage-summary fetches can be in flight at once (app
  // boot + page mount + a CellPage force-refresh) and resolve out of order — and
  // applyUsageHeaders may already have counted a just-opened cell before a slower
  // boot snapshot lands. So a payload reporting FEWER views than we already hold
  // is stale: applying it would roll the meter (and derived `remaining`) backwards
  // and could wrongly re-unlock a cell. We ignore that regression. A genuine reset
  // (viewed_count === 0) is always honoured. Mirrors the viewed_today UNION below.
  const incomingUsed = typeof json.viewed_count === 'number' ? json.viewed_count : null
  const usedIsStale = incomingUsed != null && incomingUsed !== 0 && incomingUsed < viewsUsed.value

  if (incomingUsed != null && !usedIsStale) viewsUsed.value = incomingUsed
  // Keep the last-known-good cap on a partial/invalid limit (see applyUsageHeaders).
  if (typeof json.daily_limit === 'number' && isValidLimit(json.daily_limit)) {
    viewsLimit.value = json.daily_limit
    // Authoritative per-account cap → the meter may now show the real denominator.
    viewsLimitHydrated.value = true
  }
  // Trust the payload's `remaining` only when we accepted its `viewed_count`
  // (they come from the same snapshot and must stay consistent); otherwise derive
  // `remaining` from the EFFECTIVE (possibly retained) used count so a rejected
  // stale used never leaves a mismatched remaining behind.
  if (!usedIsStale && typeof json.remaining === 'number') {
    viewsRemaining.value = json.remaining
  } else if (
    typeof json.daily_limit === 'number' &&
    isValidLimit(json.daily_limit) &&
    json.daily_limit !== UNLIMITED
  ) {
    viewsRemaining.value = Math.max(0, json.daily_limit - viewsUsed.value)
  }
  if (json.reset_at) resetAtUtc.value = json.reset_at
  // The viewed_today LIST only ever grows within a UTC day — a cell is never
  // "un-viewed" mid-day. So within the day we UNION the incoming list with the
  // one we already hold (deduped by product_id) rather than overwriting it.
  // This makes the store race-safe: a late/partial usage-summary response (or a
  // parallel force-refresh from opening several cells fast) can no longer drop a
  // just-opened / optimistically-inserted cell and wrongly re-lock it. Only a
  // reset/day-change (viewed_count === 0) clears the list to the backend value.
  if (Array.isArray(json.viewed_today)) {
    if (json.viewed_count === 0) {
      viewedToday.value = json.viewed_today
    } else {
      const seen = new Set()
      const merged = []
      for (const c of [...(viewedToday.value || []), ...json.viewed_today]) {
        const pid = c && (c.product_id || c.id)
        if (!pid || seen.has(pid)) continue
        seen.add(pid)
        merged.push(c)
      }
      viewedToday.value = merged
    }
  }
}

/**
 * Apply the `{ cell, usage }` payload from POST /products/demo/unlock. `usage`
 * has the same shape as the usage-summary endpoint, so this simply routes it
 * through applyUsageSummary — updating viewsUsed/viewsLimit/viewedToday (and
 * therefore unlockedTodayIds) so the unlock becomes visible everywhere the
 * store is read, not just in the surface that triggered it. No-op when the
 * payload doesn't carry `usage` (e.g. an unexpected/error shape).
 *
 * Also bumps `demoUnlockVersion` so reactive consumers (MainAppPage's explorer
 * reload) know a cell was unlocked — only on a genuine successful unlock, not
 * on the no-op branch.
 */
export function applyUnlockResult(json) {
  if (json && json.usage) {
    applyUsageSummary(json.usage)
    demoUnlockVersion.value++
  }
}

/**
 * Detail-page unlock → auto-refetch bridge (demo-scraping-friction follow-up,
 * Task A1). Unlike the in-place unlock (Grid/Table/Plot; `applyUnlockResult`
 * above), a Deep-Dive consumed via the CELL DETAIL PAGE (`/explorer?cell=…`)
 * never goes through `applyUnlockResult` — the detail GET itself consumes the
 * token server-side, and the caller (CellPage) only calls
 * `refreshDemoUsageSummary(true)` (→ `applyUsageSummary`) to re-sync
 * `viewedToday`. `applyUsageSummary` deliberately does NOT bump
 * `demoUnlockVersion` itself — it also runs on every ordinary re-open/
 * hydration, so bumping there would refetch the explorer on every detail
 * load, not just genuine unlocks. This helper is the narrow bridge: it bumps
 * `demoUnlockVersion` ONLY on a genuine new-unlock transition — was-not-
 * unlocked → now-unlocked — so MainAppPage's reactive effect reloads the
 * explorer and the exact premium values replace the teaser placeholders,
 * exactly like the in-place path.
 *
 * The caller MUST capture `wasUnlocked` via `isCellUnlockedToday(productId)`
 * BEFORE the detail load starts — i.e. before `markCellViewedToday` /
 * `refreshDemoUsageSummary` have had a chance to add the cell to
 * `viewedToday` — otherwise the transition is unobservable.
 *
 * No-op for:
 *   - non-demo users (`isDemoCatalogUnlocked()` false)
 *   - a cell already unlocked before this load (`wasUnlocked` true) — a
 *     re-open of an already-viewed-today cell must not trigger a refetch
 *   - the cell still absent from `viewedToday` (the load didn't actually
 *     consume a token — e.g. it hit the daily limit)
 *
 * @param {string} productId
 * @param {boolean} wasUnlocked - isCellUnlockedToday(productId) captured
 *   before the detail load started
 */
export function notifyDetailUnlock(productId, wasUnlocked) {
  if (!isDemoCatalogUnlocked()) return
  if (wasUnlocked) return
  if (!isCellUnlockedToday(productId)) return
  demoUnlockVersion.value++
}

/**
 * Optimistically record a cell as viewed-today the instant its detail opens, so
 * the DEMO grid lock-affordance never depends on the async usage-summary
 * round-trip (which can race/overwrite). Idempotent by product_id; the
 * localStorage mirror is persisted automatically by the effect() above whenever
 * viewedToday changes. No-op for non-demo users.
 * @param {object} cell - { product_id | id, name?, manufacturer? }
 */
export function markCellViewedToday(cell) {
  if (!isDemoCatalogUnlocked()) return
  const pid = cell && (cell.product_id || cell.id)
  if (!pid) return
  const list = viewedToday.value || []
  if (list.some((c) => (c.product_id || c.id) === pid)) return
  viewedToday.value = [
    ...list,
    {
      product_id: pid,
      name: cell.name || null,
      manufacturer: cell.manufacturer || null,
      viewed_at: null,
    },
  ]
}

/**
 * Reconcile the store with a 429 demo_daily_view_limit payload and decide
 * whether to slam the full modal (first hit of the day) or a slim toast
 * (subsequent same-day hits — no nagging).
 *
 * @param {object} [payload] - { viewed_count, daily_limit, reset_at }
 */
export function openLimitModalOrToast(payload = {}) {
  if (typeof payload.viewed_count === 'number') viewsUsed.value = payload.viewed_count
  if (typeof payload.daily_limit === 'number') {
    viewsLimit.value = payload.daily_limit
    viewsRemaining.value = 0
    // A 429 carries the authoritative account cap → hydrate the meter denominator.
    viewsLimitHydrated.value = true
  }
  if (payload.reset_at) resetAtUtc.value = payload.reset_at

  // BA decision (Fix-Paket 2, Punkt 7): every blocked deep-dive shows the full
  // conversion card — not just the first hit of the day. The earlier same-day
  // "slim toast" downgrade is gone, so the card (with its Talk-to-team / Get PRO
  // CTAs + reminder opt-in) is the consistent conversion surface on every block.
  // limitHitOnceToday is still tracked (analytics / reset), but no longer gates
  // which surface we show. demoLimitToastOpen is forced closed so no stale toast
  // from another path lingers behind the modal.
  limitHitOnceToday.value = true
  demoLimitModalOpen.value = true
  demoLimitToastOpen.value = false

  // Bug A (deadlock break): a *bare* lock-click lands here (payload carries no
  // server data) because the store still holds an old, lower cap with
  // remaining<=0. If an admin RAISED the limit mid-session, the server would
  // actually serve the cell — but the lock-click opened this modal instead of
  // loading it, so the only mid-session refresh path is blocked. Optimistically
  // re-pull the authoritative usage-summary so the NEXT attempt unlocks without
  // a reload. Skipped on the 429 path (payload already carries the fresh cap).
  // Fire-and-forget, demo-guarded, never throws. Dynamic import avoids a
  // store→service circular dependency (the service imports this module).
  if (typeof payload.daily_limit !== 'number' && isDemoCatalogUnlocked()) {
    import('../services/demoLimitService.js')
      .then(({ refreshDemoUsageSummary }) => refreshDemoUsageSummary(true))
      .catch(() => {
        /* non-fatal — the modal is already open */
      })
  }
}

export function closeLimitModal() {
  demoLimitModalOpen.value = false
}

// Global handler for ECharts tooltip HTML strings, which can't wire a Preact
// onClick — same pattern as window.__toggleCompare / window.__ionViewCell.
// Registered once here (this module is always loaded wherever a plot tooltip
// needs to gate a locked cell's "View Details" link) so the Ragone/Free/Score/
// My plot tooltips can open the conversion modal instead of navigating to a
// cell the user is locked out of for the day.
if (typeof window !== 'undefined' && !window.__openDemoLimitModal) {
  window.__openDemoLimitModal = () => openLimitModalOrToast({})
}

export function closeLimitToast() {
  demoLimitToastOpen.value = false
}

/**
 * Full reset — used on logout so a new session starts clean.
 */
export function reset() {
  viewsUsed.value = 0
  viewsLimit.value = UNLIMITED
  viewsRemaining.value = UNLIMITED
  viewsLimitHydrated.value = false
  resetAtUtc.value = null
  viewedToday.value = []
  demoLimitModalOpen.value = false
  demoLimitToastOpen.value = false
  limitHitOnceToday.value = false
  exportUpsellOpen.value = false
  exportUpsellSurface.value = null
  metricUpsellOpen.value = false
  metricUpsellCategory.value = null
  metricUpsellSeenOnce.value = false
  try {
    safeLocal.remove(STORAGE_KEY)
  } catch (e) {
    /* non-fatal */
  }
}

/** True when the demo user has exhausted today's deep-dive quota. */
export function isAtDeepDiveLimit() {
  if (!isDemoCatalogUnlocked()) return false
  return viewsRemaining.value !== UNLIMITED && viewsRemaining.value <= 0
}

/**
 * True when a cell should render a lock affordance: only for DEMO users who are
 * at the limit AND have not already opened this cell today (idempotent re-opens
 * stay free, matching the backend cell_view_quota semantics). Purely derived.
 */
export function isCellLockedAtLimit(productId) {
  if (!isAtDeepDiveLimit()) return false
  const opened = viewedToday.value || []
  return !opened.some((c) => (c.product_id || c.id) === productId)
}

/**
 * Whole hours until the UTC reset boundary (for "resets in Xh" copy).
 * Falls back to next UTC midnight when resetAtUtc is unknown.
 */
export function hoursUntilReset(now = new Date()) {
  let target
  if (resetAtUtc.value) {
    target = new Date(resetAtUtc.value)
  } else {
    // Next UTC midnight.
    target = new Date(now)
    target.setUTCHours(24, 0, 0, 0)
  }
  const ms = target.getTime() - now.getTime()
  if (!Number.isFinite(ms) || ms <= 0) return 0
  return Math.max(1, Math.ceil(ms / (1000 * 60 * 60)))
}
